import { type FormEvent, useMemo, useState } from 'react';
import type { RegraSetup, RegraSetupNova, RetornoLinha } from '@pcp/contracts';
import { useGravarLote, useMaquinas, useSetup, useTiposTroca, isOk } from '../../api/cadastros';
import { type Column, EditableTable } from '../../components/EditableTable';
import { ErrorState, LoadingBlock } from '../../components/states';
import { t } from '../../i18n/pt-BR';
import { num } from './shared';

const c = t.cadastros.matrix;
const GROUPS = ['CAL', 'LAM', 'COA', 'SLT'] as const;

const EMPTY: RegraSetupNova = {
  maquina: '*',
  'tipo-troca': '',
  'valor-de': '*',
  'valor-para': '*',
  'setup-min': 0,
  'perda-kg': 0,
  permitido: true,
};

const key = (r: RegraSetup) => `${r.maquina}|${r['tipo-troca']}|${r['valor-de']}|${r['valor-para']}`;

/** Matriz de troca (de/para). O ABL não grava grupo: o filtro por grupo usa o grupo da máquina. */
export function SetupTab({ canEdit }: { canEdit: boolean }) {
  const query = useSetup();
  const machines = useMaquinas();
  const types = useTiposTroca();
  const lote = useGravarLote('setup');
  const [group, setGroup] = useState('');
  const [machine, setMachine] = useState('');
  const [draft, setDraft] = useState<RegraSetupNova>(EMPTY);
  const [result, setResult] = useState<RetornoLinha | null>(null);

  const groupOf = useMemo(
    () => new Map((machines.data ?? []).map((m) => [m.maquina, m.grupo] as const)),
    [machines.data],
  );
  const rows = (query.data ?? []).filter(
    (r) =>
      (!machine || r.maquina === machine || r.maquina === '*') &&
      (!group || r.maquina === '*' || groupOf.get(r.maquina) === group),
  );

  const columns: Column<RegraSetup>[] = [
    { key: 'maquina', header: c.machine, read: (r) => <strong>{r.maquina}</strong> },
    { key: 'tipo-troca', header: c.type, read: (r) => r['tipo-troca'] },
    { key: 'valor-de', header: c.from, read: (r) => r['valor-de'] },
    { key: 'valor-para', header: c.to, read: (r) => r['valor-para'] },
    {
      key: 'setup-min',
      header: c.minutes,
      numeric: true,
      read: (r) => num(r['setup-min']),
      edit: { kind: 'number', field: 'setup-min', step: 5, min: 0 },
    },
    {
      key: 'perda-kg',
      header: c.lossKg,
      numeric: true,
      read: (r) => num(r['perda-kg']),
      edit: { kind: 'number', field: 'perda-kg', step: 1, min: 0 },
    },
    {
      key: 'permitido',
      header: c.allowed,
      read: (r) => (r.permitido ? t.cadastros.yes : <span className="chip bad">{t.cadastros.no}</span>),
      edit: { kind: 'check', field: 'permitido' },
    },
    {
      key: 'observacao',
      header: c.note,
      read: (r) => r.observacao ?? '',
      edit: { kind: 'text', field: 'observacao', width: 200 },
    },
  ];

  const save = async (row: RegraSetup, changes: Partial<RegraSetup>) => {
    const [r] = await lote.mutateAsync({ metodo: 'PUT', linhas: [{ ...row, ...changes }] });
    return r!;
  };
  const remove = async (row: RegraSetup) => {
    const [r] = await lote.mutateAsync({ metodo: 'DELETE', linhas: [row] });
    return r!;
  };
  const create = async (e: FormEvent) => {
    e.preventDefault();
    const [r] = await lote.mutateAsync({ metodo: 'POST', linhas: [draft] });
    setResult(r!);
    if (r && isOk(r)) setDraft(EMPTY);
  };

  return (
    <section className="panel" aria-labelledby="tab-matrix">
      <h2 id="tab-matrix">{t.cadastros.tabs.setup}</h2>
      <p className="hint">{c.hint}</p>
      <div className="row">
        <label className="inline-label" htmlFor="mx-group">
          {c.group}
        </label>
        <select id="mx-group" className="select" value={group} onChange={(e) => setGroup(e.target.value)}>
          <option value="">{t.cadastros.all}</option>
          {GROUPS.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        <label className="inline-label" htmlFor="mx-machine">
          {c.machine}
        </label>
        <select
          id="mx-machine"
          className="select"
          value={machine}
          onChange={(e) => setMachine(e.target.value)}
        >
          <option value="">{t.cadastros.all}</option>
          {(machines.data ?? [])
            .filter((m) => !group || m.grupo === group)
            .map((m) => (
              <option key={m.maquina} value={m.maquina}>
                {m.descricao}
              </option>
            ))}
        </select>
      </div>
      {query.isLoading ? <LoadingBlock /> : null}
      {query.error ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.data ? (
        <EditableTable
          rows={rows}
          rowKey={key}
          rowLabel={(r) => `${r.maquina} ${r['tipo-troca']} ${r['valor-de']}→${r['valor-para']}`}
          columns={columns}
          canEdit={canEdit}
          onSave={save}
          onDelete={remove}
          onReload={() => void query.refetch()}
        />
      ) : null}

      {canEdit ? (
        <form onSubmit={(e) => void create(e)} className="new-row" aria-label={c.newTitle}>
          <strong>{c.newTitle}</strong>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="ns-maquina">{c.machine}</label>
              <select
                id="ns-maquina"
                className="select"
                value={draft.maquina}
                onChange={(e) => setDraft((d) => ({ ...d, maquina: e.target.value }))}
              >
                <option value="*">* (todas)</option>
                {(machines.data ?? []).map((m) => (
                  <option key={m.maquina} value={m.maquina}>
                    {m.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="ns-tipo">{c.type}</label>
              <select
                id="ns-tipo"
                className="select"
                value={draft['tipo-troca']}
                onChange={(e) => setDraft((d) => ({ ...d, 'tipo-troca': e.target.value }))}
              >
                <option value="" />
                {(types.data ?? []).map((tp) => (
                  <option key={tp['tipo-troca']} value={tp['tipo-troca']}>
                    {tp['tipo-troca']}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="ns-de">{c.from}</label>
              <input
                id="ns-de"
                value={draft['valor-de']}
                onChange={(e) => setDraft((d) => ({ ...d, 'valor-de': e.target.value }))}
              />
            </div>
            <div className="field">
              <label htmlFor="ns-para">{c.to}</label>
              <input
                id="ns-para"
                value={draft['valor-para']}
                onChange={(e) => setDraft((d) => ({ ...d, 'valor-para': e.target.value }))}
              />
            </div>
            <div className="field">
              <label htmlFor="ns-min">{c.minutes}</label>
              <input
                id="ns-min"
                type="number"
                min={0}
                value={draft['setup-min']}
                onChange={(e) => setDraft((d) => ({ ...d, 'setup-min': Number(e.target.value) }))}
              />
            </div>
            <div className="field" style={{ alignSelf: 'end' }}>
              <button type="submit" className="btn" disabled={!draft['tipo-troca'] || lote.isPending}>
                {t.cadastros.add}
              </button>
            </div>
          </div>
          {result && !isOk(result) ? (
            <div className="form-error" role="alert">
              {result.errordescription}
            </div>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}
